import axios from "axios";

import ROUTES from "shared/config/apiRoutes";
import { getGrowthBookInstance } from "shared/utils/growthbook";
import { transformRoute } from "shared/utils/routing";
import { VeroFlag } from "shared/utils/veroFlags";

import { API_URL } from "./setupApi";

// When the configurable-doc-quantities flag is off, scrub the required_qty and runway_months fields.
const scrubQuantityFieldsIfFlagOff = (dealSetup: any) => {
  if (!dealSetup?.requiredAttachments) return dealSetup;
  let quantitiesOn = false;
  let runwayOn = false;
  try {
    const gb = getGrowthBookInstance();
    quantitiesOn = gb.getFeatureValue(
      VeroFlag.useConfigurableDocQuantities,
      false
    );
    runwayOn = gb.getFeatureValue(VeroFlag.useDocumentRunwayCalculation, false);
  } catch {
    // GrowthBook unavailable (e.g. in tests) — fall through and scrub to
    // match the safer default (legacy payload shape).
  }
  if (quantitiesOn || runwayOn) return dealSetup;
  return {
    ...dealSetup,
    requiredAttachments: dealSetup.requiredAttachments.map((ar: any) => {
      const copy = { ...ar };
      delete copy.requiredQty;
      delete copy.runwayMonths;
      return copy;
    }),
  };
};

export const getDefaultDealSetup = async () =>
  axios.get(`${API_URL}${ROUTES.defaultDealSetup}`).then(({ data }) => data);

export const getDefaultPrescreeningDealSetup = async () =>
  axios
    .get(`${API_URL}${ROUTES.defaultPrescreeningDealSetup}`)
    .then(({ data }) => data);

export const getDealSetup = async ({ id }) =>
  axios
    .get(`${API_URL}${transformRoute(ROUTES.dealSetup, { id })}`)
    .then(({ data }) => data);

export const getDealSetups = async ({ propertyId }) =>
  axios.get(`${API_URL}${ROUTES.dealSetups}`, {
    // @ts-ignore
    property_id: propertyId,
  });

export const updateDealSetup = async ({ id, dealSetup }) =>
  axios.post(
    `${API_URL}${transformRoute(ROUTES.updateDealSetup, { id })}`,
    scrubQuantityFieldsIfFlagOff(dealSetup)
  );
export const copyDealSetupFromPortfolio = async ({ propertyId }) =>
  axios
    .post(
      `${API_URL}${transformRoute(ROUTES.copyDealSetupFromPortfolio, {
        propertyId,
      })}`
    )
    .then(({ data }) => data);
export const resetPropertyToPortfolioSettings = ({ propertyId }) =>
  axios
    .delete(
      `${API_URL}${transformRoute(ROUTES.resetPropertyToPortfolioSettings, {
        propertyId,
      })}`
    )
    .then(({ data }) => data);
export const copyPrescreeningDealSetupFromPortfolio = ({ propertyId }) =>
  axios
    .post(
      `${API_URL}${transformRoute(
        ROUTES.copyPrescreeningDealSetupFromPortfolio,
        {
          propertyId,
        }
      )}`
    )
    .then(({ data }) => data);
export const resetToPrescreeningPortfolioSettings = ({ propertyId }) =>
  axios
    .delete(
      `${API_URL}${transformRoute(ROUTES.resetToPrescreeningPortfolioSettings, {
        propertyId,
      })}`
    )
    .then(({ data }) => data);
export const getPortfolioDealSetup = ({ portfolioId }) =>
  axios.get(
    `${API_URL}${transformRoute(ROUTES.portfolioDealSetup, { portfolioId })}`
  );
export const toggleRequiredQuestionActive = ({ id, requiredQuestionsId }) =>
  axios.patch(
    `${API_URL}${transformRoute(ROUTES.toggleRequiredQuestionActive, {
      id,
    })}`,
    {
      requiredQuestionsId,
    }
  );
export const enableLROSettings = ({ id, enable }) =>
  axios
    .patch(`${API_URL}${transformRoute(ROUTES.enableLROConfig, { id })}`, {
      enable,
    })
    .then(({ data }) => data);
export const updateLROSettings = ({ id, enabled }) =>
  axios
    .post(
      `${API_URL}${transformRoute(ROUTES.updateLROConfig, { id })}`,
      enabled
    )
    .then(({ data }) => data);
export type V1ExpressionType = "applicant" | "occupant" | "guarantor" | "lead";

export interface V1ExpressionsParams {
  id: number;
  types: V1ExpressionType[];
}

export const previewV1Expressions = async ({ id, types }: V1ExpressionsParams) =>
  axios
    .post(`${API_URL}${transformRoute(ROUTES.previewV1Expressions, { id })}`, {
      types,
    })
    .then(({ data }) => data);

export const deployV1Expressions = async ({ id, types }: V1ExpressionsParams) =>
  axios
    .post(`${API_URL}${transformRoute(ROUTES.deployV1Expressions, { id })}`, {
      types,
    })
    .then(({ data }) => data);
